/* START: express cart tracking */
/**
 * WFW cart delivery-status tracking.
 * One cart note-attribute key (same place as Helium _helium_user / _helium_session):
 *   delivery-status → JSON { pin, handle: { type, sku }, ... }
 * Session cache + fingerprint avoids dual /cart/update after ATC then cart open.
 */
(function () {
  'use strict';

  if (document.documentElement.classList.contains('h-ab-live')) return;

  var ATTR_STATUS = 'delivery-status';
  /** Legacy keys from earlier draft — cleared on flush so only one key remains. */
  var LEGACY_ATTRS = ['_wfw_delivery_status', '_wfw_delivery_pin'];
  var CACHE_KEY = 'WfwDeliveryCartTrack:v1';
  var SESSION_SKU_PREFIX = 'WfwSkuStatus:v1:';
  /** Must match delivery-sla.js / collection-delivery.js (`pin::sku`). */
  var SKU_KEY_SEP = '::';
  var PIN_KEY = 'ExistPincode';
  var SKU_CACHE_TTL = 5 * 60 * 1000;
  var API_BASE = window.WfwDeliveryStatusUrl;
  /* LIVE status host is snippets/delivery-status-url.liquid
  var API_BASE = 'https://impulse-backend.heliumbuilder.com/api/delivery/status';
  */
  var SHOP_DOMAIN = 'shopforw.myshopify.com';
  var SKU_CHUNK_SIZE = 300;
  var TYPES = { SDD: 'SDD', NDD: 'NDD', ND: 'ND' };

  var state = {
    pin: '',
    byHandle: {},
    syncedFingerprint: '',
  };
  var flushTimer = null;
  var flushInflight = null;
  var hooksInstalled = false;
  var jqueryHookInstalled = false;
  var pubsubHookInstalled = false;

  /* ---------- type utils ---------- */

  function normalizeType(raw) {
    if (raw == null) return null;
    if (typeof raw === 'object') {
      var fromObj = normalizeType(raw.deliveryType || raw.type);
      if (fromObj) return fromObj;
      if (raw.totalHours != null) return typeFromHours(raw.totalHours);
      return null;
    }
    var s = String(raw).trim().toUpperCase();
    if (s === TYPES.SDD || s === 'SAME DAY DELIVERY') return TYPES.SDD;
    if (s === TYPES.NDD || s === 'NEXT DAY DELIVERY') return TYPES.NDD;
    if (s === TYPES.ND || s === 'NORMAL DELIVERY' || s === 'STANDARD') return TYPES.ND;
    var asNum = Number(raw);
    if (isFinite(asNum)) return typeFromHours(asNum);
    return null;
  }

  function typeFromHours(totalHours) {
    var hours = Number(totalHours);
    if (!isFinite(hours)) return null;
    if (hours < 24) return TYPES.SDD;
    if (hours <= 48) return TYPES.NDD;
    return TYPES.ND;
  }

  function isSdd(type) {
    return normalizeType(type) === TYPES.SDD;
  }

  function isNdd(type) {
    return normalizeType(type) === TYPES.NDD;
  }

  /* ---------- attribute / cache utils ---------- */

  function parseStatusAttr(str) {
    if (!str || typeof str !== 'string') {
      return { pin: '', byHandle: {} };
    }
    try {
      var parsed = JSON.parse(str);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { pin: '', byHandle: {} };
      }
      var pin = parsed.pin != null ? String(parsed.pin).trim() : '';
      // Prefer nested byHandle; also accept flat { pin, handle: { type, sku } }.
      var source =
        parsed.byHandle && typeof parsed.byHandle === 'object'
          ? parsed.byHandle
          : parsed;
      var out = {};
      Object.keys(source).forEach(function (handle) {
        if (handle === 'pin' || handle === 'byHandle') return;
        var row = source[handle];
        if (!row || typeof row !== 'object') return;
        var type = normalizeType(row.type);
        var sku = row.sku != null ? String(row.sku).trim() : '';
        if (!type && !sku) return;
        out[handle] = { type: type || null, sku: sku || '' };
      });
      return { pin: pin, byHandle: out };
    } catch (e) {
      return { pin: '', byHandle: {} };
    }
  }

  function serializeStatus(map) {
    var pin = map && map.pin != null ? String(map.pin).trim() : state.pin || '';
    var byHandle = (map && map.byHandle) || {};
    // Flat shape: { pin, handle: { type, sku }, ... } — one note-attribute key.
    var payload = { pin: pin || '' };
    Object.keys(byHandle).forEach(function (handle) {
      if (!handle || handle === 'pin') return;
      var row = byHandle[handle];
      if (!row) return;
      payload[handle] = {
        type: normalizeType(row.type),
        sku: row.sku != null ? String(row.sku).trim() : '',
      };
    });
    return JSON.stringify(payload);
  }

  function readFromCart(cart) {
    var attrs = (cart && cart.attributes) || {};
    var parsed = parseStatusAttr(attrs[ATTR_STATUS]);
    // Migrate legacy two-key draft if present.
    if (!Object.keys(parsed.byHandle).length && attrs._wfw_delivery_status) {
      parsed = parseStatusAttr(attrs._wfw_delivery_status);
      if (!parsed.pin && attrs._wfw_delivery_pin) {
        parsed.pin = String(attrs._wfw_delivery_pin).trim();
      }
    }
    return {
      pin: parsed.pin || '',
      byHandle: parsed.byHandle,
    };
  }

  function loadCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      state.pin = parsed.pin != null ? String(parsed.pin) : '';
      state.byHandle =
        parsed.byHandle && typeof parsed.byHandle === 'object' ? parsed.byHandle : {};
      state.syncedFingerprint =
        parsed.syncedFingerprint != null ? String(parsed.syncedFingerprint) : '';
    } catch (e) {}
  }

  function saveCache() {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          pin: state.pin,
          byHandle: state.byHandle,
          syncedFingerprint: state.syncedFingerprint,
        })
      );
    } catch (e) {}
  }

  function fingerprint() {
    var handles = Object.keys(state.byHandle).sort();
    var parts = [state.pin || ''];
    handles.forEach(function (h) {
      var row = state.byHandle[h] || {};
      parts.push(h + ':' + (row.type || '') + ':' + (row.sku || ''));
    });
    return parts.join('|');
  }

  function getPin() {
    if (state.pin) return state.pin;
    try {
      var stored = localStorage.getItem(PIN_KEY);
      if (stored && /^\d{6}$/.test(String(stored).trim())) {
        return String(stored).trim();
      }
    } catch (e) {}
    var api = window.WFWDeliverySlaApi;
    if (api && typeof api.getPincode === 'function') {
      var fromApi = api.getPincode();
      if (fromApi) return String(fromApi).trim();
    }
    return '';
  }

  function getTypeByHandle(handle) {
    if (!handle) return null;
    var row = state.byHandle[String(handle)];
    return row ? normalizeType(row.type) : null;
  }

  function upsert(entry) {
    if (!entry || !entry.handle) return false;
    var handle = String(entry.handle).trim();
    if (!handle) return false;
    var type = normalizeType(entry.type);
    var sku = entry.sku != null ? String(entry.sku).trim() : '';
    var prev = state.byHandle[handle];
    if (
      prev &&
      normalizeType(prev.type) === type &&
      String(prev.sku || '') === sku
    ) {
      return false;
    }
    state.byHandle[handle] = { type: type, sku: sku };
    saveCache();
    scheduleFlush();
    return true;
  }

  /* ---------- status resolve (reuse caches) ---------- */

  function skuCacheKey(pin, sku) {
    return String(pin) + SKU_KEY_SEP + String(sku);
  }

  function readSkuCacheEntry(pin, sku) {
    if (!pin || !sku) return null;
    var key = skuCacheKey(pin, sku);
    try {
      var raw = sessionStorage.getItem(SESSION_SKU_PREFIX + key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.ts >= SKU_CACHE_TTL) return null;
      return parsed.value || null;
    } catch (e) {
      return null;
    }
  }

  function writeSkuCacheEntry(pin, sku, value) {
    if (!pin || !sku) return;
    var key = skuCacheKey(pin, sku);
    try {
      sessionStorage.setItem(
        SESSION_SKU_PREFIX + key,
        JSON.stringify({ ts: Date.now(), value: value })
      );
    } catch (e) {}
  }

  /** Fallback when live theme does not expose getStatuses on WFWDeliverySlaApi. */
  async function fetchStatusesDirect(skus, pincode) {
    var unique = Array.from(new Set((skus || []).filter(Boolean)));
    var map = {};
    if (!unique.length || !/^\d{6}$/.test(String(pincode || '').trim())) {
      return { map: map };
    }
    var pin = String(pincode).trim();
    for (var i = 0; i < unique.length; i += SKU_CHUNK_SIZE) {
      var chunk = unique.slice(i, i + SKU_CHUNK_SIZE);
      try {
        var url = new URL(API_BASE);
        url.searchParams.set('skus', chunk.join(','));
        url.searchParams.set('deliveryPostcode', pin);
        url.searchParams.set('shop', SHOP_DOMAIN);
        var res = await fetch(url.toString());
        if (!res.ok) continue;
        var json = await res.json();
        if (!json.successful || !Array.isArray(json.results)) continue;
        json.results.forEach(function (row) {
          if (!row || !row.productSku) return;
          var sku = String(row.productSku);
          var value = {
            deliveryType: row.deliveryType,
            totalHours: row.totalHours,
            estimatedDeliveryDate: row.estimatedDeliveryDate,
          };
          map[sku] = value;
          writeSkuCacheEntry(pin, sku, value);
        });
        chunk.forEach(function (sku) {
          if (!map[sku]) writeSkuCacheEntry(pin, sku, null);
        });
      } catch (e) {}
    }
    return { map: map };
  }

  function getStatusesFn() {
    if (
      window.WFWDeliverySlaApi &&
      typeof window.WFWDeliverySlaApi.getStatuses === 'function'
    ) {
      return window.WFWDeliverySlaApi.getStatuses.bind(window.WFWDeliverySlaApi);
    }
    if (
      window.WFWCollectionDeliveryStatus &&
      typeof window.WFWCollectionDeliveryStatus.getStatuses === 'function'
    ) {
      return window.WFWCollectionDeliveryStatus.getStatuses.bind(
        window.WFWCollectionDeliveryStatus
      );
    }
    return fetchStatusesDirect;
  }

  function handleFromItem(item) {
    if (!item || typeof item !== 'object') return '';
    if (item.handle) return String(item.handle).trim();
    var url = item.url || item.product_url || '';
    try {
      var m = String(url).match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) {
      return '';
    }
  }

  async function resolveTypesForLines(lines, pin) {
    var result = {};
    if (!lines || !lines.length) return result;

    // Always seed handles from cart lines (even without pin/sku/type).
    lines.forEach(function (line) {
      if (!line || !line.handle) return;
      result[line.handle] = {
        type: null,
        sku: line.sku ? String(line.sku) : '',
      };
    });

    if (!pin) return result;

    var missing = [];
    lines.forEach(function (line) {
      if (!line || !line.handle || !line.sku) return;
      var cached = readSkuCacheEntry(pin, line.sku);
      var type = normalizeType(cached);
      if (type) {
        result[line.handle] = { type: type, sku: String(line.sku) };
      } else {
        missing.push(line);
      }
    });

    if (!missing.length) return result;

    var getStatuses = getStatusesFn();
    if (!getStatuses) return result;

    var skus = missing.map(function (l) {
      return l.sku;
    });
    try {
      var status = await getStatuses(skus, pin);
      var map = (status && status.map) || {};
      missing.forEach(function (line) {
        var row = map[line.sku];
        var type = normalizeType(row);
        result[line.handle] = {
          type: type,
          sku: String(line.sku),
        };
      });
    } catch (e) {}

    return result;
  }

  function linesFromCart(cart) {
    var items = (cart && cart.items) || [];
    return items
      .map(function (item) {
        var handle = handleFromItem(item);
        var sku = item.sku != null ? String(item.sku).trim() : '';
        if (!handle) return null;
        return { handle: handle, sku: sku };
      })
      .filter(Boolean);
  }

  function linesFromAddResponse(json) {
    if (!json || typeof json !== 'object') return [];
    var items = [];
    if (Array.isArray(json.items) && json.items.length) {
      items = json.items;
    } else if (json.handle || json.url || json.variant_id || json.id) {
      items = [json];
    }
    return items
      .map(function (item) {
        var handle = handleFromItem(item);
        var sku = item.sku != null ? String(item.sku).trim() : '';
        if (!handle) return null;
        return { handle: handle, sku: sku };
      })
      .filter(Boolean);
  }

  /* ---------- cart attribute flush ---------- */

  function scheduleFlush() {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(function () {
      flushTimer = null;
      flush();
    }, 120);
  }

  var NOTES_CLEARED_KEY = 'WfwOrderNotesCleared:v1';

  function ownedNoteKeys(attrs) {
    var next = {};
    var dirty = false;
    [ATTR_STATUS].concat(LEGACY_ATTRS).forEach(function (key) {
      if (attrs && attrs[key]) {
        next[key] = '';
        dirty = true;
      }
    });
    var feature = attrs && attrs._helium_feature;
    if (feature === 'delivery_check' || feature === 'pincode') {
      next._helium_feature = '';
      next._helium_feature_id = '';
      next._helium_attribution = '';
      dirty = true;
    }
    return dirty ? next : null;
  }

  function flush() {
    try {
      if (sessionStorage.getItem(NOTES_CLEARED_KEY) === '1') {
        return Promise.resolve({ skipped: true });
      }
    } catch (e) {}
    if (flushInflight) return flushInflight;

    flushInflight = fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart read failed');
        return res.json();
      })
      .then(function (cart) {
        var next = ownedNoteKeys((cart && cart.attributes) || {});
        if (!next) {
          try {
            sessionStorage.setItem(NOTES_CLEARED_KEY, '1');
          } catch (e) {}
          return { skipped: true };
        }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: next }),
        }).then(function (res) {
          if (!res.ok) throw new Error('cart update failed');
          try {
            sessionStorage.setItem(NOTES_CLEARED_KEY, '1');
          } catch (e) {}
          return { skipped: false };
        });
      })
      .catch(function () {
        return { skipped: false, error: true };
      })
      .finally(function () {
        flushInflight = null;
      });

    return flushInflight;
  }

  async function syncCart(cart) {
    if (!cart) {
      try {
        var res = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        cart = await res.json();
      } catch (e) {
        return;
      }
    }

    var pin = getPin();
    state.pin = pin || state.pin;

    var lines = linesFromCart(cart);
    var handlesInCart = {};
    lines.forEach(function (l) {
      handlesInCart[l.handle] = true;
    });

    // Prune handles no longer in cart.
    Object.keys(state.byHandle).forEach(function (h) {
      if (!handlesInCart[h]) delete state.byHandle[h];
    });

    // Always record cart handles first; then enrich types when pin/status available.
    if (lines.length) {
      var resolved = await resolveTypesForLines(lines, pin);
      Object.keys(resolved).forEach(function (handle) {
        var row = resolved[handle];
        var prev = state.byHandle[handle];
        state.byHandle[handle] = {
          type: normalizeType(row.type) || (prev && prev.type) || null,
          sku: row.sku || (prev && prev.sku) || '',
        };
      });
    }

    if (!state.pin) {
      var fromCart = readFromCart(cart);
      if (fromCart.pin) state.pin = fromCart.pin;
    }

    saveCache();
    return flush();
  }

  async function trackLines(lines) {
    if (!lines || !lines.length) return;
    var pin = getPin();
    state.pin = pin || state.pin;
    var resolved = await resolveTypesForLines(lines, pin);
    Object.keys(resolved).forEach(function (handle) {
      var row = resolved[handle];
      var prev = state.byHandle[handle];
      state.byHandle[handle] = {
        type: normalizeType(row.type) || (prev && prev.type) || null,
        sku: row.sku || (prev && prev.sku) || '',
      };
    });
    saveCache();
    return flush();
  }

  /* ---------- hooks ---------- */

  function urlLooksLike(url, needle) {
    try {
      var u = typeof url === 'string' ? url : url && url.url ? url.url : '';
      return String(u).indexOf(needle) !== -1;
    } catch (e) {
      return false;
    }
  }

  function onCartNetworkSuccess(reqUrl, json) {
    var isAdd = urlLooksLike(reqUrl, '/cart/add');
    var isCartJs =
      urlLooksLike(reqUrl, '/cart.js') &&
      !urlLooksLike(reqUrl, '/cart/add') &&
      !urlLooksLike(reqUrl, '/cart/update') &&
      !urlLooksLike(reqUrl, '/cart/change') &&
      !urlLooksLike(reqUrl, '/cart/clear');
    var isChange =
      urlLooksLike(reqUrl, '/cart/change') ||
      urlLooksLike(reqUrl, '/cart/clear');

    if (isAdd) {
      var lines = linesFromAddResponse(json);
      if (lines.length) trackLines(lines);
      else syncCart();
      return;
    }
    if (isCartJs || isChange) {
      if (json && Array.isArray(json.items)) syncCart(json);
      else syncCart();
    }
  }

  function installFetchHook() {
    if (hooksInstalled || typeof window.fetch !== 'function') return;
    hooksInstalled = true;
    var originalFetch = window.fetch.bind(window);

    function stripOwnedLineProperties(init) {
      if (!init || init.body == null) return;
      var owned = /pincode|express|delivery|_helium_feature|_helium_attribution/i;
      if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
        var drop = [];
        init.body.forEach(function (_value, key) {
          if (/^properties\[/i.test(key) && owned.test(key)) drop.push(key);
        });
        drop.forEach(function (key) {
          init.body.delete(key);
        });
        return;
      }
      if (typeof init.body !== 'string') return;
      try {
        var json = JSON.parse(init.body);
        if (json && json.properties && typeof json.properties === 'object') {
          Object.keys(json.properties).forEach(function (key) {
            if (owned.test(key)) delete json.properties[key];
          });
          init.body = JSON.stringify(json);
        }
      } catch (e) {
        init.body = init.body.replace(
          /(?:^|&)properties\[[^\]]*(?:pincode|express|delivery|_helium_feature|_helium_attribution)[^\]]*\]=[^&]*/gi,
          ''
        );
      }
    }

    window.fetch = function (input, init) {
      var reqUrl =
        typeof input === 'string'
          ? input
          : input && input.url
            ? input.url
            : '';
      if (urlLooksLike(reqUrl, '/cart/add')) stripOwnedLineProperties(init);

      return originalFetch(input, init).then(function (response) {
        try {
          if (
            response &&
            response.ok &&
            (urlLooksLike(reqUrl, '/cart/add') ||
              urlLooksLike(reqUrl, '/cart.js') ||
              urlLooksLike(reqUrl, '/cart/change') ||
              urlLooksLike(reqUrl, '/cart/clear'))
          ) {
            response
              .clone()
              .json()
              .then(function (json) {
                onCartNetworkSuccess(reqUrl, json);
              })
              .catch(function () {
                if (!urlLooksLike(reqUrl, '/cart/update')) syncCart();
              });
          }
        } catch (e) {}
        return response;
      });
    };
  }

  function installJqueryHook() {
    if (jqueryHookInstalled) return;
    var $ = window.jQuery || window.$;
    if (!$ || typeof $.fn === 'undefined') return;
    jqueryHookInstalled = true;
    $(document).ajaxSuccess(function (_ev, _xhr, settings, data) {
      try {
        var url = (settings && settings.url) || '';
        if (!urlLooksLike(url, '/cart/')) return;
        onCartNetworkSuccess(url, data);
      } catch (e) {}
    });
  }

  function installPubSubHook() {
    if (pubsubHookInstalled) return;
    try {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
        return;
      }
      pubsubHookInstalled = true;
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        syncCart();
      });
    } catch (e) {}
  }

  function onPincodeUpdated(ev) {
    var pin =
      ev && ev.detail && ev.detail.pincode
        ? String(ev.detail.pincode).trim()
        : getPin();
    if (state.pin && pin && state.pin !== pin) {
      // Keep handles; re-resolve types for new pin.
      state.syncedFingerprint = '';
      Object.keys(state.byHandle).forEach(function (h) {
        if (state.byHandle[h]) state.byHandle[h].type = null;
      });
    }
    state.pin = pin || '';
    saveCache();
    syncCart();
  }

  function init() {
    loadCache();
    installFetchHook();
    installJqueryHook();
    installPubSubHook();
    document.addEventListener('wfw:pincode-updated', onPincodeUpdated);

    // Initial sync if cart already has items (e.g. return visitor).
    var boot = function () {
      syncCart();
      // Late jQuery / pubsub may load after us.
      setTimeout(installJqueryHook, 500);
      setTimeout(installPubSubHook, 500);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }

  init();

  window.WFWDeliveryCartTrack = {
    TYPES: TYPES,
    ATTR_STATUS: ATTR_STATUS,
    normalizeType: normalizeType,
    isSdd: isSdd,
    isNdd: isNdd,
    parseStatusAttr: parseStatusAttr,
    serializeStatus: serializeStatus,
    getTypeByHandle: getTypeByHandle,
    getPin: getPin,
    readFromCart: readFromCart,
    upsert: upsert,
    syncCart: syncCart,
    flush: flush,
    getMap: function () {
      return {
        pin: state.pin || getPin(),
        byHandle: Object.assign({}, state.byHandle),
      };
    },
  };
})();
/* END: express cart tracking */

/* LIVE cart tracking — express test control arm */
/**
 * WFW cart delivery-status tracking.
 * One cart note-attribute key (same place as Helium _helium_user / _helium_session):
 *   delivery-status → JSON { pin, handle: { type, sku }, ... }
 * Session cache + fingerprint avoids dual /cart/update after ATC then cart open.
 */
(function () {
  'use strict';

  if (!document.documentElement.classList.contains('h-ab-live')) return;

  var ATTR_STATUS = 'delivery-status';
  /** Legacy keys from earlier draft — cleared on flush so only one key remains. */
  var LEGACY_ATTRS = ['_wfw_delivery_status', '_wfw_delivery_pin'];
  var CACHE_KEY = 'WfwDeliveryCartTrack:v1';
  var SESSION_SKU_PREFIX = 'WfwSkuStatus:v1:';
  /** Must match delivery-sla.js / collection-delivery.js (`pin::sku`). */
  var SKU_KEY_SEP = '::';
  var PIN_KEY = 'ExistPincode';
  var SKU_CACHE_TTL = 5 * 60 * 1000;
  var API_BASE = window.WfwDeliveryStatusUrl;
  /* LIVE status host is snippets/delivery-status-url.liquid
  var API_BASE = 'https://impulse-backend.heliumbuilder.com/api/delivery/status';
  */
  // var API_BASE = 'https://backend-pre-prod.up.railway.app/api/delivery/status';
  var SHOP_DOMAIN = 'shopforw.myshopify.com';
  var SKU_CHUNK_SIZE = 300;
  var TYPES = { SDD: 'SDD', NDD: 'NDD', ND: 'ND' };

  var state = {
    pin: '',
    byHandle: {},
    syncedFingerprint: '',
  };
  var flushTimer = null;
  var flushInflight = null;
  var hooksInstalled = false;
  var jqueryHookInstalled = false;
  var pubsubHookInstalled = false;

  /* ---------- type utils ---------- */

  function normalizeType(raw) {
    if (raw == null) return null;
    if (typeof raw === 'object') {
      var fromObj = normalizeType(raw.deliveryType || raw.type);
      if (fromObj) return fromObj;
      if (raw.totalHours != null) return typeFromHours(raw.totalHours);
      return null;
    }
    var s = String(raw).trim().toUpperCase();
    if (s === TYPES.SDD || s === 'SAME DAY DELIVERY') return TYPES.SDD;
    if (s === TYPES.NDD || s === 'NEXT DAY DELIVERY') return TYPES.NDD;
    if (s === TYPES.ND || s === 'NORMAL DELIVERY' || s === 'STANDARD') return TYPES.ND;
    var asNum = Number(raw);
    if (isFinite(asNum)) return typeFromHours(asNum);
    return null;
  }

  function typeFromHours(totalHours) {
    var hours = Number(totalHours);
    if (!isFinite(hours)) return null;
    if (hours < 24) return TYPES.SDD;
    if (hours <= 48) return TYPES.NDD;
    return TYPES.ND;
  }

  function isSdd(type) {
    return normalizeType(type) === TYPES.SDD;
  }

  function isNdd(type) {
    return normalizeType(type) === TYPES.NDD;
  }

  /* ---------- attribute / cache utils ---------- */

  function parseStatusAttr(str) {
    if (!str || typeof str !== 'string') {
      return { pin: '', byHandle: {} };
    }
    try {
      var parsed = JSON.parse(str);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { pin: '', byHandle: {} };
      }
      var pin = parsed.pin != null ? String(parsed.pin).trim() : '';
      // Prefer nested byHandle; also accept flat { pin, handle: { type, sku } }.
      var source =
        parsed.byHandle && typeof parsed.byHandle === 'object'
          ? parsed.byHandle
          : parsed;
      var out = {};
      Object.keys(source).forEach(function (handle) {
        if (handle === 'pin' || handle === 'byHandle') return;
        var row = source[handle];
        if (!row || typeof row !== 'object') return;
        var type = normalizeType(row.type);
        var sku = row.sku != null ? String(row.sku).trim() : '';
        if (!type && !sku) return;
        out[handle] = { type: type || null, sku: sku || '' };
      });
      return { pin: pin, byHandle: out };
    } catch (e) {
      return { pin: '', byHandle: {} };
    }
  }

  function serializeStatus(map) {
    var pin = map && map.pin != null ? String(map.pin).trim() : state.pin || '';
    var byHandle = (map && map.byHandle) || {};
    // Flat shape: { pin, handle: { type, sku }, ... } — one note-attribute key.
    var payload = { pin: pin || '' };
    Object.keys(byHandle).forEach(function (handle) {
      if (!handle || handle === 'pin') return;
      var row = byHandle[handle];
      if (!row) return;
      payload[handle] = {
        type: normalizeType(row.type),
        sku: row.sku != null ? String(row.sku).trim() : '',
      };
    });
    return JSON.stringify(payload);
  }

  function readFromCart(cart) {
    var attrs = (cart && cart.attributes) || {};
    var parsed = parseStatusAttr(attrs[ATTR_STATUS]);
    // Migrate legacy two-key draft if present.
    if (!Object.keys(parsed.byHandle).length && attrs._wfw_delivery_status) {
      parsed = parseStatusAttr(attrs._wfw_delivery_status);
      if (!parsed.pin && attrs._wfw_delivery_pin) {
        parsed.pin = String(attrs._wfw_delivery_pin).trim();
      }
    }
    return {
      pin: parsed.pin || '',
      byHandle: parsed.byHandle,
    };
  }

  function loadCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      state.pin = parsed.pin != null ? String(parsed.pin) : '';
      state.byHandle =
        parsed.byHandle && typeof parsed.byHandle === 'object' ? parsed.byHandle : {};
      state.syncedFingerprint =
        parsed.syncedFingerprint != null ? String(parsed.syncedFingerprint) : '';
    } catch (e) {}
  }

  function saveCache() {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          pin: state.pin,
          byHandle: state.byHandle,
          syncedFingerprint: state.syncedFingerprint,
        })
      );
    } catch (e) {}
  }

  function fingerprint() {
    var handles = Object.keys(state.byHandle).sort();
    var parts = [state.pin || ''];
    handles.forEach(function (h) {
      var row = state.byHandle[h] || {};
      parts.push(h + ':' + (row.type || '') + ':' + (row.sku || ''));
    });
    return parts.join('|');
  }

  function getPin() {
    if (state.pin) return state.pin;
    try {
      var stored = localStorage.getItem(PIN_KEY);
      if (stored && /^\d{6}$/.test(String(stored).trim())) {
        return String(stored).trim();
      }
    } catch (e) {}
    var api = window.WFWDeliverySlaApi;
    if (api && typeof api.getPincode === 'function') {
      var fromApi = api.getPincode();
      if (fromApi) return String(fromApi).trim();
    }
    return '';
  }

  function getTypeByHandle(handle) {
    if (!handle) return null;
    var row = state.byHandle[String(handle)];
    return row ? normalizeType(row.type) : null;
  }

  function upsert(entry) {
    if (!entry || !entry.handle) return false;
    var handle = String(entry.handle).trim();
    if (!handle) return false;
    var type = normalizeType(entry.type);
    var sku = entry.sku != null ? String(entry.sku).trim() : '';
    var prev = state.byHandle[handle];
    if (
      prev &&
      normalizeType(prev.type) === type &&
      String(prev.sku || '') === sku
    ) {
      return false;
    }
    state.byHandle[handle] = { type: type, sku: sku };
    saveCache();
    scheduleFlush();
    return true;
  }

  /* ---------- status resolve (reuse caches) ---------- */

  function skuCacheKey(pin, sku) {
    return String(pin) + SKU_KEY_SEP + String(sku);
  }

  function readSkuCacheEntry(pin, sku) {
    if (!pin || !sku) return null;
    var key = skuCacheKey(pin, sku);
    try {
      var raw = sessionStorage.getItem(SESSION_SKU_PREFIX + key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.ts >= SKU_CACHE_TTL) return null;
      return parsed.value || null;
    } catch (e) {
      return null;
    }
  }

  function writeSkuCacheEntry(pin, sku, value) {
    if (!pin || !sku) return;
    var key = skuCacheKey(pin, sku);
    try {
      sessionStorage.setItem(
        SESSION_SKU_PREFIX + key,
        JSON.stringify({ ts: Date.now(), value: value })
      );
    } catch (e) {}
  }

  /** Fallback when live theme does not expose getStatuses on WFWDeliverySlaApi. */
  async function fetchStatusesDirect(skus, pincode) {
    var unique = Array.from(new Set((skus || []).filter(Boolean)));
    var map = {};
    if (!unique.length || !/^\d{6}$/.test(String(pincode || '').trim())) {
      return { map: map };
    }
    var pin = String(pincode).trim();
    for (var i = 0; i < unique.length; i += SKU_CHUNK_SIZE) {
      var chunk = unique.slice(i, i + SKU_CHUNK_SIZE);
      try {
        var url = new URL(API_BASE);
        url.searchParams.set('skus', chunk.join(','));
        url.searchParams.set('deliveryPostcode', pin);
        url.searchParams.set('shop', SHOP_DOMAIN);
        var res = await fetch(url.toString());
        if (!res.ok) continue;
        var json = await res.json();
        if (!json.successful || !Array.isArray(json.results)) continue;
        json.results.forEach(function (row) {
          if (!row || !row.productSku) return;
          var sku = String(row.productSku);
          var value = {
            deliveryType: row.deliveryType,
            totalHours: row.totalHours,
            estimatedDeliveryDate: row.estimatedDeliveryDate,
          };
          map[sku] = value;
          writeSkuCacheEntry(pin, sku, value);
        });
        chunk.forEach(function (sku) {
          if (!map[sku]) writeSkuCacheEntry(pin, sku, null);
        });
      } catch (e) {}
    }
    return { map: map };
  }

  function getStatusesFn() {
    if (
      window.WFWDeliverySlaApi &&
      typeof window.WFWDeliverySlaApi.getStatuses === 'function'
    ) {
      return window.WFWDeliverySlaApi.getStatuses.bind(window.WFWDeliverySlaApi);
    }
    if (
      window.WFWCollectionDeliveryStatus &&
      typeof window.WFWCollectionDeliveryStatus.getStatuses === 'function'
    ) {
      return window.WFWCollectionDeliveryStatus.getStatuses.bind(
        window.WFWCollectionDeliveryStatus
      );
    }
    return fetchStatusesDirect;
  }

  function handleFromItem(item) {
    if (!item || typeof item !== 'object') return '';
    if (item.handle) return String(item.handle).trim();
    var url = item.url || item.product_url || '';
    try {
      var m = String(url).match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) {
      return '';
    }
  }

  async function resolveTypesForLines(lines, pin) {
    var result = {};
    if (!lines || !lines.length) return result;

    // Always seed handles from cart lines (even without pin/sku/type).
    lines.forEach(function (line) {
      if (!line || !line.handle) return;
      result[line.handle] = {
        type: null,
        sku: line.sku ? String(line.sku) : '',
      };
    });

    if (!pin) return result;

    var missing = [];
    lines.forEach(function (line) {
      if (!line || !line.handle || !line.sku) return;
      var cached = readSkuCacheEntry(pin, line.sku);
      var type = normalizeType(cached);
      if (type) {
        result[line.handle] = { type: type, sku: String(line.sku) };
      } else {
        missing.push(line);
      }
    });

    if (!missing.length) return result;

    var getStatuses = getStatusesFn();
    if (!getStatuses) return result;

    var skus = missing.map(function (l) {
      return l.sku;
    });
    try {
      var status = await getStatuses(skus, pin);
      var map = (status && status.map) || {};
      missing.forEach(function (line) {
        var row = map[line.sku];
        var type = normalizeType(row);
        result[line.handle] = {
          type: type,
          sku: String(line.sku),
        };
      });
    } catch (e) {}

    return result;
  }

  function linesFromCart(cart) {
    var items = (cart && cart.items) || [];
    return items
      .map(function (item) {
        var handle = handleFromItem(item);
        var sku = item.sku != null ? String(item.sku).trim() : '';
        if (!handle) return null;
        return { handle: handle, sku: sku };
      })
      .filter(Boolean);
  }

  function linesFromAddResponse(json) {
    if (!json || typeof json !== 'object') return [];
    var items = [];
    if (Array.isArray(json.items) && json.items.length) {
      items = json.items;
    } else if (json.handle || json.url || json.variant_id || json.id) {
      items = [json];
    }
    return items
      .map(function (item) {
        var handle = handleFromItem(item);
        var sku = item.sku != null ? String(item.sku).trim() : '';
        if (!handle) return null;
        return { handle: handle, sku: sku };
      })
      .filter(Boolean);
  }

  /* ---------- cart attribute flush ---------- */

  function scheduleFlush() {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(function () {
      flushTimer = null;
      flush();
    }, 120);
  }

  function flush() {
    var fp = fingerprint();
    if (fp === state.syncedFingerprint) {
      return Promise.resolve({ skipped: true });
    }
    if (flushInflight) return flushInflight;

    var body = {
      attributes: {},
    };
    body.attributes[ATTR_STATUS] = serializeStatus({
      pin: state.pin || getPin() || '',
      byHandle: state.byHandle,
    });
    // Clear legacy two-key draft so only delivery-status remains.
    LEGACY_ATTRS.forEach(function (key) {
      body.attributes[key] = '';
    });

    flushInflight = fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart update failed');
        return res.json();
      })
      .then(function () {
        state.syncedFingerprint = fp;
        saveCache();
        return { skipped: false };
      })
      .catch(function () {
        return { skipped: false, error: true };
      })
      .finally(function () {
        flushInflight = null;
      });

    return flushInflight;
  }

  async function syncCart(cart) {
    if (!cart) {
      try {
        var res = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        cart = await res.json();
      } catch (e) {
        return;
      }
    }

    var pin = getPin();
    state.pin = pin || state.pin;

    var lines = linesFromCart(cart);
    var handlesInCart = {};
    lines.forEach(function (l) {
      handlesInCart[l.handle] = true;
    });

    // Prune handles no longer in cart.
    Object.keys(state.byHandle).forEach(function (h) {
      if (!handlesInCart[h]) delete state.byHandle[h];
    });

    // Always record cart handles first; then enrich types when pin/status available.
    if (lines.length) {
      var resolved = await resolveTypesForLines(lines, pin);
      Object.keys(resolved).forEach(function (handle) {
        var row = resolved[handle];
        var prev = state.byHandle[handle];
        state.byHandle[handle] = {
          type: normalizeType(row.type) || (prev && prev.type) || null,
          sku: row.sku || (prev && prev.sku) || '',
        };
      });
    }

    if (!state.pin) {
      var fromCart = readFromCart(cart);
      if (fromCart.pin) state.pin = fromCart.pin;
    }

    saveCache();
    return flush();
  }

  async function trackLines(lines) {
    if (!lines || !lines.length) return;
    var pin = getPin();
    state.pin = pin || state.pin;
    var resolved = await resolveTypesForLines(lines, pin);
    Object.keys(resolved).forEach(function (handle) {
      var row = resolved[handle];
      var prev = state.byHandle[handle];
      state.byHandle[handle] = {
        type: normalizeType(row.type) || (prev && prev.type) || null,
        sku: row.sku || (prev && prev.sku) || '',
      };
    });
    saveCache();
    return flush();
  }

  /* ---------- hooks ---------- */

  function urlLooksLike(url, needle) {
    try {
      var u = typeof url === 'string' ? url : url && url.url ? url.url : '';
      return String(u).indexOf(needle) !== -1;
    } catch (e) {
      return false;
    }
  }

  function onCartNetworkSuccess(reqUrl, json) {
    var isAdd = urlLooksLike(reqUrl, '/cart/add');
    var isCartJs =
      urlLooksLike(reqUrl, '/cart.js') &&
      !urlLooksLike(reqUrl, '/cart/add') &&
      !urlLooksLike(reqUrl, '/cart/update') &&
      !urlLooksLike(reqUrl, '/cart/change') &&
      !urlLooksLike(reqUrl, '/cart/clear');
    var isChange =
      urlLooksLike(reqUrl, '/cart/change') ||
      urlLooksLike(reqUrl, '/cart/clear');

    if (isAdd) {
      var lines = linesFromAddResponse(json);
      if (lines.length) trackLines(lines);
      else syncCart();
      return;
    }
    if (isCartJs || isChange) {
      if (json && Array.isArray(json.items)) syncCart(json);
      else syncCart();
    }
  }

  function installFetchHook() {
    if (hooksInstalled || typeof window.fetch !== 'function') return;
    hooksInstalled = true;
    var originalFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      var reqUrl =
        typeof input === 'string'
          ? input
          : input && input.url
            ? input.url
            : '';

      return originalFetch(input, init).then(function (response) {
        try {
          if (
            response &&
            response.ok &&
            (urlLooksLike(reqUrl, '/cart/add') ||
              urlLooksLike(reqUrl, '/cart.js') ||
              urlLooksLike(reqUrl, '/cart/change') ||
              urlLooksLike(reqUrl, '/cart/clear'))
          ) {
            response
              .clone()
              .json()
              .then(function (json) {
                onCartNetworkSuccess(reqUrl, json);
              })
              .catch(function () {
                if (!urlLooksLike(reqUrl, '/cart/update')) syncCart();
              });
          }
        } catch (e) {}
        return response;
      });
    };
  }

  function installJqueryHook() {
    if (jqueryHookInstalled) return;
    var $ = window.jQuery || window.$;
    if (!$ || typeof $.fn === 'undefined') return;
    jqueryHookInstalled = true;
    $(document).ajaxSuccess(function (_ev, _xhr, settings, data) {
      try {
        var url = (settings && settings.url) || '';
        if (!urlLooksLike(url, '/cart/')) return;
        onCartNetworkSuccess(url, data);
      } catch (e) {}
    });
  }

  function installPubSubHook() {
    if (pubsubHookInstalled) return;
    try {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
        return;
      }
      pubsubHookInstalled = true;
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        syncCart();
      });
    } catch (e) {}
  }

  function onPincodeUpdated(ev) {
    var pin =
      ev && ev.detail && ev.detail.pincode
        ? String(ev.detail.pincode).trim()
        : getPin();
    if (state.pin && pin && state.pin !== pin) {
      // Keep handles; re-resolve types for new pin.
      state.syncedFingerprint = '';
      Object.keys(state.byHandle).forEach(function (h) {
        if (state.byHandle[h]) state.byHandle[h].type = null;
      });
    }
    state.pin = pin || '';
    saveCache();
    syncCart();
  }

  function init() {
    loadCache();
    installFetchHook();
    installJqueryHook();
    installPubSubHook();
    document.addEventListener('wfw:pincode-updated', onPincodeUpdated);

    // Initial sync if cart already has items (e.g. return visitor).
    var boot = function () {
      syncCart();
      // Late jQuery / pubsub may load after us.
      setTimeout(installJqueryHook, 500);
      setTimeout(installPubSubHook, 500);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }

  init();

  window.WFWDeliveryCartTrack = {
    TYPES: TYPES,
    ATTR_STATUS: ATTR_STATUS,
    normalizeType: normalizeType,
    isSdd: isSdd,
    isNdd: isNdd,
    parseStatusAttr: parseStatusAttr,
    serializeStatus: serializeStatus,
    getTypeByHandle: getTypeByHandle,
    getPin: getPin,
    readFromCart: readFromCart,
    upsert: upsert,
    syncCart: syncCart,
    flush: flush,
    getMap: function () {
      return {
        pin: state.pin || getPin(),
        byHandle: Object.assign({}, state.byHandle),
      };
    },
  };
})();
